import { useParams, useLocation } from "wouter";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GlobalHeader } from "@/components/GlobalHeader";
import { Sidebar, MobileSidebar } from "@/components/Sidebar";

import ComparisonTabs from "@/components/comparison/ComparisonTabs";

import { cn } from "@/lib/utils";
import { useComparisonData } from "@/hooks/useComparisonData";

export default function ComparisonWorkspace() {
    const { claimId: claimIdStr, comparisonId: comparisonIdStr } = useParams<{ claimId: string, comparisonId: string }>();
    const claimId = parseInt(claimIdStr || "0");
    const comparisonId = parseInt(comparisonIdStr || "0");

    const [, setLocation] = useLocation();
    const [mobileNavOpen, setMobileNavOpen] = useState(false);

    // Use custom hook for data fetching and transformation
    const {
        transformedData,
        isLoading,
        isProcessing,
        claim,
        comparisonData
    } = useComparisonData(claimId, comparisonId);
    const canWrite = claim?.canWrite ?? false;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen bg-background">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    // Fallback if data is missing after loading
    if (!comparisonData || !transformedData) {
        return (
            <div className="flex flex-col items-center justify-center h-screen bg-background text-muted-foreground">
                <p>Comparison data not found.</p>
                <Button variant="link" onClick={() => setLocation(`/claims/${claimId}`)}>
                    Go back to claim
                </Button>
            </div>
        );
    }

    return (
        <div className="flex h-[100dvh] md:h-screen bg-slate-50 dark:bg-slate-950 overflow-hidden">
            <Sidebar />
            <MobileSidebar open={mobileNavOpen} onOpenChange={setMobileNavOpen} />

            <div className="flex-1 flex flex-col min-w-0 bg-slate-50 dark:bg-slate-950 overflow-hidden relative">
                <GlobalHeader onOpenMobileNav={() => setMobileNavOpen(true)} />

                {/* Processing/Locking Overlay Banner */}
                {isProcessing && (
                    <div className="bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 sm:px-6 py-2 flex items-center justify-center gap-2 text-sm text-amber-800 dark:text-amber-200 animate-in slide-in-from-top-0 duration-300">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="font-medium text-center">AI Analysis in progress... The dashboard will update automatically.</span>
                    </div>
                )}

                {claim && !canWrite && (
                    <div className="bg-amber-100 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 sm:px-6 py-2 flex items-center justify-center text-sm text-amber-800 dark:text-amber-200">
                        <span className="font-medium text-center">View-only access: comparison editing actions are blocked.</span>
                    </div>
                )}

                {/* Workspace Content - No top padding, let sticky header hit top-0 of this scroll area */}
                <div className={cn(
                    "flex-1 overflow-auto transition-opacity duration-200",
                    isProcessing ? "opacity-70 pointer-events-none grayscale-[0.3]" : ""
                )}>
                    <div className="max-w-7xl mx-auto px-3 sm:px-6 pb-6">
                        <ComparisonTabs
                            comparisonId={comparisonId}
                            claimId={claimId}
                            claimNumber={claim?.claimNumber || claimId.toString()}
                            isProcessing={isProcessing}
                            canWrite={canWrite}
                            stats={transformedData.stats}
                            enhancedMatches={transformedData.enhancedMatches}
                            hydratedMatchGroups={transformedData.hydratedMatchGroups}
                            organizedRooms={transformedData.organizedRooms}
                            unmatchedLineItemsA={transformedData.unmatchedLineItemsA}
                            unmatchedLineItemsB={transformedData.unmatchedLineItemsB}
                            allRoomsList={transformedData.allRooms}
                            unmatchedInsurerRooms={transformedData.unmatchedInsurerRooms}
                            unmatchedContractorRooms={transformedData.unmatchedContractorRooms}
                            insurerEstimateId={comparisonData?.comparison?.insurerEstimateId}
                            contractorEstimateId={comparisonData?.comparison?.contractorEstimateId}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
}
